import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Book, CoverSettings } from '../types';
import { getGraphQLClient, GET_BOOK, SAVE_PROCESSED_COVER, removeBookCover } from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { Spinner } from '../components/common';
import CoverEditorFull from '../components/books/CoverEditorFull';
import CoverImageSection from '../components/books/CoverImageSection';

export default function CoverEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bookId } = useParams<{ bookId: string }>();
  const [book, setBook] = useState<Book | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBook = useCallback(async () => {
    if (!bookId) return;

    try {
      const client = getGraphQLClient();
      const data = await client.request<{ book: Book }>(GET_BOOK, { id: bookId });
      setBook(data.book);
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  }, [bookId, t]);

  useEffect(() => {
    loadBook();
  }, [loadBook]);

  const handleSave = async (imageData: string, settings: CoverSettings) => {
    if (!bookId) return;

    setIsSaving(true);
    setError(null);

    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        saveProcessedCover: {
          coverImageUrl: string | null;
          errors: string[];
        };
      }>(SAVE_PROCESSED_COVER, {
        bookId,
        imageData,
        coverSettings: settings,
      });

      if (data.saveProcessedCover.errors.length > 0) {
        setError(data.saveProcessedCover.errors[0]);
      } else {
        // Navigate back to the book page
        navigate(`/books/${bookId}`);
      }
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCoverChange = (url: string | null) => {
    if (url) {
      // Reload book data to get the new cover
      loadBook();
    }
  };

  const handleRemove = async () => {
    if (!bookId) return;

    setIsRemoving(true);
    setError(null);

    try {
      await removeBookCover(bookId);
      // Reload book data to show the cover upload interface
      await loadBook();
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500 dark:text-gray-400">{t('common.error')}</p>
      </div>
    );
  }

  // Determine the editable image URL
  const editableImageUrl = book.coverImageOriginalUrl || book.coverImageUrl;

  if (!editableImageUrl) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="flex items-center mb-6">
            <button
              onClick={handleCancel}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {t('common.back')}
            </button>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
              {t('books.coverImage')}
            </h1>
            <CoverImageSection
              bookId={bookId!}
              coverImageUrl={book.coverImageUrl}
              coverImageOriginalUrl={book.coverImageOriginalUrl}
              onCoverChange={handleCoverChange}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <CoverEditorFull
      bookId={bookId!}
      originalImageUrl={editableImageUrl}
      title={book.title || ''}
      subtitle={book.subtitle}
      authorName={book.user?.name || ''}
      bookType={book.bookType}
      savedSettings={book.coverSettings}
      onSave={handleSave}
      onCancel={handleCancel}
      onRemove={handleRemove}
      isSaving={isSaving}
      isRemoving={isRemoving}
      error={error}
    />
  );
}
